"use client"

import { Popover, Transition } from "@headlessui/react"
import { Button } from "@medusajs/ui"
import { usePathname } from "next/navigation"
import { Fragment, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { getGroupItemHash } from "@lib/util/groupItemHash"
import { convertToLocale } from "@lib/util/money"
import { resolveImageUrl } from "@lib/util/image-url"
import DeleteButton from "@modules/common/components/delete-button"
import LineItemOptions from "@modules/common/components/line-item-options"
import Thumbnail from "@modules/products/components/thumbnail"
import Link from "next/link"

import { Button as ShadcnButton } from "@/components/ui/button"
import { usePriceChangeDetection } from "@/hooks/use-price-change-detection"
import { StoreCart, StoreCartItem } from "@/types/store"
import { enrichLineItemsWithCartPrices, fetchCart, mergeGuestCartToUserCart, retrieveCart } from "@lib/data/cart"
import {
  clientRemoveGuestCartIdFromCookies,
  getGuestCartIdFromCookies,
  getMedusaCartIdFromCookies
} from "@lib/data/client-cookies"
import { calculateSubtotal } from "@lib/util/discountUtils"
import { HttpTypes } from "@medusajs/types"
import { PriceChangeModal } from "@modules/cart/components/price-change-modal"
import PlaceholderImage from "@modules/common/icons/placeholder-image"
import { RefreshCcw, ShoppingCart } from "lucide-react"
import { normalizeTextEncoding } from "@/lib/util/text-encoding"

const CartDropdown = ({ cart, customer }: { cart?: StoreCart | null, customer?: any }) => {
  const { t } = useTranslation()
  const [activeTimer, setActiveTimer] = useState<NodeJS.Timer | undefined>(
    undefined
  )
  const [cartDropdownOpen, setCartDropdownOpen] = useState(false)
  const [cartState, setCartState] = useState<StoreCart | null>(cart ?? null)
  const [mounted, setMounted] = useState(false)
  const { showPriceChangeModal, checkForPriceChanges, closePriceChangeModal } = usePriceChangeDetection()

  const open = () => setCartDropdownOpen(true)
  const close = () => setCartDropdownOpen(false)

  const handleCartMerge = async () => {
    try {
      const guestCartId = await getGuestCartIdFromCookies()
      const medusaCartId = await getMedusaCartIdFromCookies()

      if (guestCartId && medusaCartId && guestCartId !== medusaCartId) {

        const currentCart = await fetchCart()
        const hasPartialPayment = (currentCart?.walletPaymentAmount && currentCart.walletPaymentAmount > 0)

        if (hasPartialPayment) {
          return false
        }

        await mergeGuestCartToUserCart(guestCartId)
        clientRemoveGuestCartIdFromCookies()
        return true
      }
    } catch (err: any) {
      console.error("Failed to merge guest cart to user cart: " + err.message)
    }
    return false
  }

  const refetchCart = async () => {
    try {
      const merged = await handleCartMerge()

      const updatedCart = await retrieveCart()
      if (updatedCart?.items?.length) {
        const enrichedItems = await enrichLineItemsWithCartPrices(updatedCart?.items)
        updatedCart.items = enrichedItems as HttpTypes.StoreCartLineItem[]
      }

      // Check for price changes using centralized detection
      // Note: This is only called when not on cart/checkout pages (see onMouseEnter)
      checkForPriceChanges(updatedCart)

      setCartState(updatedCart)
    } catch (err: any) {
      console.error("Failed to update cart: " + err.message)
    }
  }

  // Grouping logic to match CartNewTemplate
  const newOrderItems = cartState?.items?.reduce(
    (acc: (StoreCartItem & { matchingIds?: number[] })[], item) => {
      const hash = getGroupItemHash({
        variantId: item.variant.id,
        formAnswers: (item as any).form_answers,
        salesPrice: item.variant.sales_price,
      })
      const existingItem = acc.find(
        (i) =>
          getGroupItemHash({
            variantId: i.variant.id,
            formAnswers: (i as any).form_answers,
            salesPrice: i.variant.sales_price,
          }) === hash
      )
      if (!existingItem) {
        const clonedVariant = { ...item.variant, quantity: item.variant.quantity }
        acc.push({ ...item, variant: clonedVariant, matchingIds: [item.item_id] })
      } else {
        existingItem.matchingIds = existingItem.matchingIds || []
        existingItem.matchingIds.push(item.item_id)
        if (existingItem.variant && item.variant) {
          existingItem.variant.quantity += item.variant.quantity
        }
      }
      return acc
    },
    []
  )

  const totalItems =
    newOrderItems?.reduce((acc, item) => {
      return acc + item.variant.quantity
    }, 0) || 0

  const total = calculateSubtotal(cartState)
  const itemRef = useRef<number>(totalItems || 0)

  const timedOpen = () => {
    open()

    const timer = setTimeout(close, 5000)

    setActiveTimer(timer)
  }

  const openAndCancel = () => {
    if (activeTimer) {
      clearTimeout(activeTimer)
    }

    open()
  }

  // Clean up the timer when the component unmounts
  useEffect(() => {
    return () => {
      if (activeTimer) {
        clearTimeout(activeTimer)
      }
    }
  }, [activeTimer])

  const pathname = usePathname()

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    const handleExternalOpen = async () => {
      if (shouldRefetchCart()) {
        await refetchCart()
      }
    }

    window.addEventListener("cart:open", handleExternalOpen)
    return () => window.removeEventListener("cart:open", handleExternalOpen)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  // // open cart dropdown when modifying the cart items, but only if we're not on the cart page
  // useEffect(() => {
  //   if (itemRef.current !== totalItems && pathname && !pathname.includes("/cart")) {
  //     timedOpen()
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [totalItems, itemRef.current])

  // Skip cart refetch on cart/checkout pages to avoid duplicate API calls
  // Those pages handle their own cart fetching and price change detection
  const shouldRefetchCart = () => {
    const currentPath = pathname || (typeof window !== 'undefined' ? window.location.pathname : '')
    return !currentPath.includes("/cart") && !currentPath.includes("/checkout")
  }

  return (
    mounted && (
    <div
      className="z-40 h-full"
      onMouseEnter={() => {
        openAndCancel()
        // Only refetch cart if we're not on cart/checkout pages
        // This avoids duplicate API calls and price change detection conflicts
        if (shouldRefetchCart()) {
          refetchCart()
        }
      }}
      onMouseLeave={close}
    >
      <Popover className="flex relative flex-col justify-center h-full border-none ring-0">
        <Link
          className="relative"
          href="/cart"
          data-testid="nav-cart-link"
        >
          <ShadcnButton
            variant="outline"
            size="sm"
            className="h-11 w-11 shrink-0 rounded-full border-orange-200 bg-white p-0 text-orange-600 shadow-sm transition-all duration-200 hover:border-orange-300 hover:bg-orange-400 hover:text-white"
          >
            <ShoppingCart className="h-5 w-5" />
          </ShadcnButton>
          {totalItems > 0 && (
            <span className="absolute -right-1.5 -top-1.5 inline-flex min-w-[18px] items-center justify-center rounded-full bg-orange-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
              {totalItems > 99 ? "99+" : totalItems}
            </span>
          )}
        </Link>
        <Transition
          show={cartDropdownOpen}
          as={Fragment}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
        >
          <Popover.Panel
            static
            className="hidden small:block absolute top-[calc(100%+8px)] right-0 w-[430px] rounded-2xl border border-slate-200 bg-slate-50 text-slate-700 shadow-2xl"
            data-testid="nav-cart-dropdown"
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <h3 className="text-base font-semibold text-slate-800">{t("cartDropdown.title")}</h3>
              <Button
                onClick={refetchCart}
                variant="transparent"
                className="h-8 w-8 rounded-full p-0 text-slate-500 hover:bg-white"
              >
                <RefreshCcw className="w-4 h-4" />
              </Button>
            </div>
            {cartState && newOrderItems?.length ? (
              <>
                <div className="grid max-h-[402px] grid-cols-1 gap-y-6 overflow-y-scroll px-4 py-3 no-scrollbar">
                  {newOrderItems
                    .sort((a, b) => {
                      return (a.variant.id ?? "") > (b.variant.id ?? "")
                        ? -1
                        : 1
                    })
                    .map((item) => {
                      let thumbnailUrl = null

                      const thumbnail =
                        item.variant.images?.find(
                          (image) => image.is_thumbnail === 1
                        ) || item.variant.images?.[0]

                      if (thumbnail) {
                        thumbnailUrl = resolveImageUrl(thumbnail.image_key)
                      }
                      // Parse form_answers safely
                      let parsedAnswers = [];
                      const formAnswersRaw = (item as any).form_answers;
                      if (Array.isArray(formAnswersRaw)) {
                        parsedAnswers = formAnswersRaw;
                      } else if (typeof formAnswersRaw === 'string' && formAnswersRaw.trim()) {
                        try {
                          const parsed = JSON.parse(formAnswersRaw);
                          parsedAnswers = Array.isArray(parsed) ? parsed : [];
                        } catch (e) {
                          parsedAnswers = [];
                        }
                      } else if (typeof formAnswersRaw === 'object' && formAnswersRaw !== null) {
                        parsedAnswers = [formAnswersRaw];
                      } else {
                        parsedAnswers = [];
                      }
                      return (
                        <div
                          className="grid grid-cols-[98px_1fr] gap-x-3 rounded-xl border border-slate-200 bg-white p-2.5"
                          key={item.variant.id + '-' + item.item_id}
                          data-testid="cart-item"
                        >
                          <Link
                            href={`/products/${item.product.handle}`}
                            className="w-24"
                          >

                            <div className="overflow-hidden relative w-24 h-24 rounded-lg border border-slate-100">
                              {thumbnailUrl ? (
                                <Thumbnail
                                  thumbnail={thumbnailUrl ?? ""}
                                  images={item.product?.images}
                                  size="square"
                                />
                              ) : (
                                <PlaceholderImage className="w-full h-full" />
                              )}
                            </div>
                          </Link>
                          <div className="flex flex-col flex-1 justify-between">
                            <div className="flex flex-col flex-1">
                              <div className="flex justify-between items-start">
                                <div className="flex flex-col overflow-ellipsis whitespace-nowrap mr-4 w-[180px] gap-y-1">
                                  <h3 className="overflow-hidden text-base-regular text-ellipsis">
                                    <Link
                                      href={`/products/${item.product?.handle}`}
                                      data-testid="product-link"
                                    >
                                      {normalizeTextEncoding(item.product.title ?? "")}
                                    </Link>
                                  </h3>
                                  <LineItemOptions
                                    title={normalizeTextEncoding(item.variant.title || "")}
                                    label={t("cartDropdown.variant")}
                                    data-testid="cart-item-variant"
                                    data-value={item.variant}
                                  />
                                  {/* Show form answers if available */}
                                  {parsedAnswers && parsedAnswers.length > 0 ? (
                                    <div className="mt-1">
                                      {parsedAnswers.map((ans: { label?: string; question?: string; answer?: string }, idx: number) => (
                                        <div key={idx} className="text-xs text-slate-500">
                                          <span className="font-medium">{normalizeTextEncoding(ans.label ?? ans.question ?? "-")}:</span> {normalizeTextEncoding(ans.answer ?? "-")}
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="mt-1 text-xs text-slate-400">{t("cartDropdown.noExtraInfo")}</div>
                                  )}
                                  <LineItemOptions
                                    title={item.variant?.quantity ?? 0}
                                    label={t("cartDropdown.quantity")}
                                    data-testid="cart-item-variant-quantity"
                                    data-value={item.variant?.quantity ?? 0}
                                  />
                                </div>
                                <div className="flex flex-col justify-between items-end h-full">
                                  {/* Use the correct price for each cart line */}
                                  <LineItemOptions
                                    title={convertToLocale({
                                      amount: item.variant.sales_price ?? 0,
                                      currency_code: cartState.currencyCode,
                                    })}
                                    label={t("cartDropdown.unitPrice")}
                                    data-testid="cart-item-variant-unit-price"
                                    data-value={item.variant.sales_price ?? 0}
                                  />
                                  {(cartState?.walletPaymentAmount ?? 0) === 0 && (
                                    <DeleteButton
                                      variantId={item.variant.id}
                                      cartItemIds={item.matchingIds || [item.item_id]}
                                      className="mt-1"
                                      data-testid="cart-item-remove-button"
                                      refetchCart={refetchCart}
                                    />
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                </div>
                <div className="relative flex flex-col gap-y-4 border-t border-slate-200 bg-white p-4 text-small-regular">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-ui-fg-base">
                      {t("cartDropdown.subtotal")}{" "}
                      <span className="font-normal">{t("cartDropdown.subtotalNote")}</span>
                    </span>
                    <span
                      className="text-large-semi"
                      data-testid="cart-total"
                      data-value={total}
                    >
                      {convertToLocale({
                        amount: total,
                        currency_code: "TRY",
                      })}
                    </span>
                  </div>
                  {(cartState?.remainingToPay ?? 0) > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-ui-fg-base">
                        {t("cartDropdown.remainingToPay")}
                      </span>
                      <span
                        className="text-large-semi"
                        data-testid="cart-remaining-to-pay"
                        data-value={cartState?.remainingToPay ?? 0}
                      >
                        {convertToLocale({
                          amount: cartState?.remainingToPay ?? 0,
                          currency_code: "TRY",
                        })}
                      </span>
                    </div>
                  )}
                  <Link href="/cart" passHref>
                    <Button
                      className="w-full rounded-xl border border-orange-300 bg-gradient-to-r from-orange-500 via-orange-400 to-amber-400 font-semibold text-white hover:from-orange-600 hover:via-orange-500 hover:to-amber-500"
                      size="large"
                      data-testid="go-to-cart-button"
                      variant="primary"
                    >
                      {t("cartDropdown.goToCart")}
                    </Button>
                  </Link>
                </div>
              </>
            ) : (
              <div>
                <div className="flex flex-col gap-y-4 justify-center items-center py-14">
                  <div className="flex justify-center items-center w-6 h-6 text-white bg-orange-500 rounded-full text-small-regular">
                    <span>0</span>
                  </div>
                  <span className="text-slate-600">Sepetiniz şu an boş.</span>
                  <div className="flex gap-x-2">
                    <Link href="/store">
                      <>
                        <span className="sr-only">Go to all products page</span>
                        <Button onClick={close} className="rounded-xl border border-orange-200 bg-white text-orange-700 hover:bg-orange-50">{t("cartDropdown.exploreProducts")}</Button>
                      </>
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </Popover.Panel>
        </Transition>
      </Popover>

      {/* Price Change Modal */}
      {cartState?.priceValidation && (
        <PriceChangeModal
          isOpen={showPriceChangeModal}
          onClose={closePriceChangeModal}
          priceValidation={cartState.priceValidation}
        />
      )}
    </div>
    )
  )
}

export default CartDropdown
